import React, { useState, useEffect, useMemo } from 'react';
import {
  IoClose,
  IoChevronDown,
  IoBuildOutline,
  IoChatbubblesOutline,
  IoCubeOutline,
  IoArrowUpCircleOutline,
  IoArrowDownCircleOutline,
} from 'react-icons/io5';
import { VscLoading } from 'react-icons/vsc';

import {
  fetchTaskTokenUsage,
  fetchWorkflowTokenUsage,
  fetchAdminTaskConversations,
} from '../../services/api';
import { CONFIG } from '../../constants/config';

// --- HELPER FUNCTIONS ---

const formatNumber = (num) => (num || 0).toLocaleString('en-US');
const formatCost = (cost) => `$${(cost || 0).toFixed(5)}`;
const calculateCost = (tokens, costPerMillion) =>
  (tokens / 1_000_000) * (costPerMillion || 0);

// --- REUSABLE UI COMPONENTS ---

/**
 * A redesigned, more spacious collapsible section.
 * It now shows separate Input/Output token counts instead of a total.
 */
const CollapsibleSection = ({
  title,
  description,
  stats,
  children,
  initialOpen = false,
  level = 0,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const levelStyles = ['bg-2', 'bg-body'];
  const paddingLeft = `${level * 1.5}rem`;

  return (
    <div className={`${levelStyles[level % 2]} rounded-lg`}>
      {/* eslint-disable-next-line */}
      <header
        style={{ paddingLeft }}
        className="flex cursor-pointer items-center justify-between p-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="min-w-0 flex-1">
          <h4 className="text-p-text truncate font-semibold">{title}</h4>
          {description && (
            <p className="text-s-text mt-1 truncate text-xs">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-4 pl-4">
          <div className="text-right">
            <p className="text-p-text text-sm font-bold">{stats.cost}</p>
            <div className="text-s-text mt-1 flex items-center justify-end gap-3 text-xs">
              <span className="flex items-center gap-1" title="Input Tokens">
                <IoArrowUpCircleOutline className="text-blue-400" />{' '}
                {formatNumber(stats.inputTokens)}
              </span>
              <span className="flex items-center gap-1" title="Output Tokens">
                <IoArrowDownCircleOutline className="text-green-400" />{' '}
                {formatNumber(stats.outputTokens)}
              </span>
            </div>
          </div>
          <IoChevronDown
            className={`text-s-text transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </header>
      {isOpen && (
        <div className="pb-2" style={{ paddingLeft }}>
          <div className="border-line ml-3 space-y-2 border-l-2 pl-3">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * A clear, table-like row for the lowest level of detail (Task or Node).
 * This component is already correctly designed.
 */
const ConversationViewer = ({ taskId, accessToken }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);

  useEffect(() => {
    const loadChats = async () => {
      setLoading(true);
      try {
        const data = await fetchAdminTaskConversations(taskId, accessToken);
        setMessages(data || []);
      } catch (err) {
        setError('Failed to load chat history.');
      } finally {
        setLoading(false);
      }
    };
    loadChats();
  }, [taskId, accessToken]);

  if (loading)
    return (
      <div className="text-s-text animate-pulse p-4 text-center text-xs">
        Loading history...
      </div>
    );
  if (error)
    return <div className="p-4 text-center text-xs text-red-400">{error}</div>;
  if (messages.length === 0)
    return (
      <div className="text-s-text p-4 text-center text-xs">
        No messages found.
      </div>
    );

  const filteredMessages = messages.filter(
    (msg) => showAssistant || msg.role === 'user',
  );

  return (
    <div className="border-line mt-4 space-y-4 border-t pt-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="group flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={showAssistant}
              onChange={(e) => setShowAssistant(e.target.checked)}
              className="h-3 w-3 accent-[#5652CF]"
            />
            <span className="text-s-text group-hover:text-p-text text-[10px] font-bold transition-colors">
              Show Agent & Tool Work
            </span>
            {CONFIG.IS_DEV_OR_DEBUG && (
              <span className="rounded-[3px] border border-orange-500/30 bg-orange-500/20 px-1 py-0.5 text-[8px] font-black uppercase tracking-tighter text-orange-400">
                Debug Mode
              </span>
            )}
          </label>
        </div>
        <span className="text-s-text font-mono text-[9px] opacity-60">
          Showing {filteredMessages.length} of {messages.length} log entries
        </span>
      </div>

      <div className="custom-scrollbar max-h-[500px] space-y-4 overflow-y-auto pr-2">
        {messages.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const isTool = msg.role === 'tool';
          const isAssistant = msg.role === 'assistant';

          if (!showAssistant && !isUser) return null;

          let content = msg.content;
          if (Array.isArray(content)) {
            content = content.map((part) => part.text || '').join('\n');
          }

          return (
            <div
              key={idx}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[90%] rounded-lg px-3 py-2 text-[11px] ${
                  isUser
                    ? 'rounded-br-none bg-[#5652CF] text-white'
                    : isTool
                      ? 'text-s-text border-line border border-dashed bg-black/20 font-mono text-[10px]'
                      : 'bg-2 text-p-text border-line rounded-bl-none border shadow-sm'
                }`}
              >
                <div
                  className={`mb-1 flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider opacity-60`}
                >
                  {msg.role}
                  {!isUser && (
                    <span className="rounded border border-orange-500/20 bg-orange-500/10 px-1 text-[7px] text-orange-500/60">
                      DEBUG
                    </span>
                  )}
                  {isAssistant && msg.tool_calls && (
                    <span className="font-normal normal-case text-blue-400">
                      — calling tools...
                    </span>
                  )}
                </div>

                {content && (
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {content}
                  </div>
                )}

                {isAssistant && msg.tool_calls && (
                  <div className="mt-2 space-y-2">
                    {msg.tool_calls.map((tc, tIdx) => {
                      let args = tc.function?.arguments || '';
                      try {
                        // Try to pretty-print JSON arguments
                        const parsed = JSON.parse(args);
                        args = JSON.stringify(parsed, null, 2);
                      } catch (e) {
                        // Keep as is if not valid JSON
                      }

                      return (
                        <div
                          key={tIdx}
                          className="rounded border border-blue-900/30 bg-black/30 px-2 py-1.5 font-mono text-[9px] text-blue-300"
                        >
                          <div className="mb-1 font-bold">
                            λ {tc.function?.name}
                          </div>
                          <pre className="whitespace-pre-wrap break-all opacity-90">
                            {args}
                          </pre>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DetailRow = ({
  title,
  prompt,
  id,
  input,
  output,
  costs,
  promptCount,
  timestamp,
  accessToken,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputCost = calculateCost(input, costs.input);
  const outputCost = calculateCost(output, costs.output);
  const totalCost = inputCost + outputCost;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div
      className={`bg-1 border-line rounded-md border p-3 transition-all ${isExpanded ? 'ring-1 ring-[#5652CF]' : 'hover:border-[#5652CF]/50'}`}
    >
      <div className="mb-3 flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <IoCubeOutline className="flex-shrink-0 text-[#5652CF]" />
            <h5 className="text-p-text truncate text-sm font-bold">{title}</h5>
            {promptCount > 1 && (
              <span className="bg-2 text-s-text rounded px-1.5 py-0.5 text-[9px] font-bold">
                {promptCount} turns
              </span>
            )}
          </div>

          <div className="mb-2 flex items-center gap-2 text-[10px] opacity-60">
            <span className="font-mono">ID: {id}</span>
            <span>•</span>
            <span>Last active: {formatDate(timestamp)}</span>
          </div>

          {prompt && (
            <div className="bg-2/50 rounded border-l-2 border-[#5652CF] p-2">
              <span className="mb-1 block text-[9px] font-bold uppercase text-[#5652CF]">
                Latest Prompt
              </span>
              <p className="text-s-text line-clamp-2 text-xs italic leading-relaxed">
                "{prompt}"
              </p>
            </div>
          )}
        </div>

        <div className="ml-4">
          {accessToken && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex items-center gap-1 rounded px-3 py-1.5 text-[10px] font-bold transition-all ${
                isExpanded
                  ? 'bg-[#5652CF] text-white shadow-lg'
                  : 'bg-2 text-[#5652CF] hover:bg-[#5652CF]/10'
              }`}
            >
              {isExpanded ? 'Close Chat' : 'Expand History'}
              <IoChevronDown
                className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-2 rounded p-2">
          <p className="text-s-text">Input</p>
          <p className="text-p-text font-semibold">{formatNumber(input)}</p>
          <p className="font-mono text-blue-400">{formatCost(inputCost)}</p>
        </div>
        <div className="bg-2 rounded p-2">
          <p className="text-s-text">Output</p>
          <p className="text-p-text font-semibold">{formatNumber(output)}</p>
          <p className="font-mono text-green-400">{formatCost(outputCost)}</p>
        </div>
        <div className="bg-2 rounded p-2">
          <p className="text-s-text">Total Cost</p>
          <p className="text-p-text font-bold">{formatCost(totalCost)}</p>
        </div>
      </div>

      {isExpanded && (
        <ConversationViewer taskId={id} accessToken={accessToken} />
      )}
    </div>
  );
};

// --- MAIN MODAL COMPONENT ---
export const TokenUsageModal = ({
  isOpen,
  onClose,
  user,
  userIdToken,
  costSettings,
}) => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      const fetchUsageData = async () => {
        setLoading(true);
        setError(null);
        try {
          const [tasks, workflows] = await Promise.all([
            fetchTaskTokenUsage(userIdToken, user.email),
            fetchWorkflowTokenUsage(userIdToken, user.email),
          ]);
          setTaskData(tasks);
          setWorkflowData(workflows);
        } catch (err) {
          setError(err.message || 'Failed to fetch usage data.');
        } finally {
          setLoading(false);
        }
      };
      fetchUsageData();
    }
  }, [isOpen, user, userIdToken]);

  const totals = useMemo(() => {
    const taskInputCost = calculateCost(
      taskData?.grand_total_input_tokens,
      costSettings.tasks.input,
    );
    const taskOutputCost = calculateCost(
      taskData?.grand_total_output_tokens,
      costSettings.tasks.output,
    );
    const wfInputCost = calculateCost(
      workflowData?.grand_total_input_tokens,
      costSettings.workflows.input,
    );
    const wfOutputCost = calculateCost(
      workflowData?.grand_total_output_tokens,
      costSettings.workflows.output,
    );

    return {
      tasks: taskInputCost + taskOutputCost,
      workflows: wfInputCost + wfOutputCost,
      grand: taskInputCost + taskOutputCost + wfInputCost + wfOutputCost,
    };
  }, [taskData, workflowData, costSettings]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading)
      return (
        <div className="text-s-text flex flex-col items-center justify-center p-10">
          <VscLoading className="mb-4 animate-spin text-4xl" />
          Loading usage data...
        </div>
      );
    if (error)
      return (
        <div className="p-10 text-center text-red-400">Error: {error}</div>
      );

    if (activeTab === 'tasks') {
      const projects = taskData?.projects_breakdown || [];
      if (projects.length === 0)
        return (
          <div className="text-s-text p-10 text-center">
            No task usage data found.
          </div>
        );
      return (
        <div className="space-y-3">
          {projects.map((proj) => {
            const projectCost =
              calculateCost(
                proj.project_total_input_tokens,
                costSettings.tasks.input,
              ) +
              calculateCost(
                proj.project_total_output_tokens,
                costSettings.tasks.output,
              );
            return (
              <CollapsibleSection
                key={proj.project_id}
                title={proj.project_name || 'Unnamed Project'}
                description={`Project ID: ${proj.project_id}`}
                stats={{
                  cost: formatCost(projectCost),
                  inputTokens: proj.project_total_input_tokens,
                  outputTokens: proj.project_total_output_tokens,
                }}
                level={0}
              >
                <div className="space-y-2">
                  {proj.tasks_breakdown.map((task) => (
                    <DetailRow
                      key={task.task_id}
                      title={task.task_name || 'Unnamed Task'}
                      prompt={task.user_prompt}
                      promptCount={task.prompt_count}
                      timestamp={task.updated_at}
                      id={task.task_id}
                      input={task.input_tokens}
                      output={task.output_tokens}
                      costs={costSettings.tasks}
                      accessToken={userIdToken}
                    />
                  ))}
                </div>
              </CollapsibleSection>
            );
          })}
        </div>
      );
    }

    if (activeTab === 'workflows') {
      const projects = workflowData?.projects_breakdown || [];
      if (projects.length === 0)
        return (
          <div className="text-s-text p-10 text-center">
            No workflow usage data found.
          </div>
        );
      return (
        <div className="space-y-3">
          {projects.map((proj) => {
            const projectCost =
              calculateCost(
                proj.project_total_input_tokens,
                costSettings.workflows.input,
              ) +
              calculateCost(
                proj.project_total_output_tokens,
                costSettings.workflows.output,
              );
            return (
              <CollapsibleSection
                key={proj.project_id}
                title={proj.project_name}
                description={`Project ID: ${proj.project_id}`}
                stats={{
                  cost: formatCost(projectCost),
                  inputTokens: proj.project_total_input_tokens,
                  outputTokens: proj.project_total_output_tokens,
                }}
                level={0}
              >
                {proj.workflows.map((wf) => {
                  const wfCost =
                    calculateCost(
                      wf.workflow_total_input,
                      costSettings.workflows.input,
                    ) +
                    calculateCost(
                      wf.workflow_total_output,
                      costSettings.workflows.output,
                    );
                  return (
                    <CollapsibleSection
                      key={wf.workflow_id}
                      title={wf.title}
                      description={wf.description}
                      stats={{
                        cost: formatCost(wfCost),
                        inputTokens: wf.workflow_total_input,
                        outputTokens: wf.workflow_total_output,
                      }}
                      level={1}
                    >
                      {wf.executions.map((exec) => {
                        const execCost =
                          calculateCost(
                            exec.execution_total_input,
                            costSettings.workflows.input,
                          ) +
                          calculateCost(
                            exec.execution_total_output,
                            costSettings.workflows.output,
                          );
                        return (
                          <CollapsibleSection
                            key={exec.execution_id}
                            title={`Execution`}
                            description={`ID: ${exec.execution_id}`}
                            stats={{
                              cost: formatCost(execCost),
                              inputTokens: exec.execution_total_input,
                              outputTokens: exec.execution_total_output,
                            }}
                            level={2}
                          >
                            <div className="space-y-2">
                              {exec.nodes.map((node) => (
                                <DetailRow
                                  key={node.node_id}
                                  title="Node"
                                  prompt={node.prompt}
                                  id={node.node_id}
                                  input={node.input_tokens}
                                  output={node.output_tokens}
                                  costs={costSettings.workflows}
                                />
                              ))}
                            </div>
                          </CollapsibleSection>
                        );
                      })}
                    </CollapsibleSection>
                  );
                })}
              </CollapsibleSection>
            );
          })}
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="bg-1 animate-fade-in-up flex h-[90vh] w-full max-w-5xl flex-col rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="border-line flex items-start justify-between border-b p-5">
          <div className="flex items-center gap-4">
            <img
              src={user.picture}
              alt={user.name}
              className="h-14 w-14 rounded-full"
            />
            <div>
              <h2 className="text-p-text text-xl font-bold">{user.name}</h2>
              <p className="text-s-text text-sm">{user.email}</p>
              <div className="mt-2 text-2xl font-bold text-green-400">
                {formatCost(totals.grand)}
                <span className="text-s-text ml-2 text-sm font-normal">
                  Total Estimated Cost
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-2 rounded-full p-2">
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-line border-b p-3">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'tasks' ? 'bg-[#5652CF] text-white' : 'hover:bg-2 text-s-text'}`}
            >
              <div className="flex items-center gap-2">
                <IoChatbubblesOutline /> Tasks{' '}
                <span className="text-xs opacity-80">
                  ({formatCost(totals.tasks)})
                </span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('workflows')}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'workflows' ? 'bg-[#5652CF] text-white' : 'hover:bg-2 text-s-text'}`}
            >
              <div className="flex items-center gap-2">
                <IoBuildOutline /> Workflows{' '}
                <span className="text-xs opacity-80">
                  ({formatCost(totals.workflows)})
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="bg-body flex-1 overflow-y-auto p-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
